import { lazy, type ComponentType } from 'react';

/**
 * Lazy load utility with retry logic for failed chunk loads
 * Implements exponential backoff for network failures
 */
export function lazyLoadWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  componentName: string,
  maxRetries = 3
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptLoad = (attemptNumber: number) => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            if (attemptNumber >= maxRetries) {
              console.error(
                `Failed to load component "${componentName}" after ${maxRetries} attempts`,
                error
              );
              reject(error);
              return;
            }

            const delay = Math.min(1000 * Math.pow(2, attemptNumber), 8000);
            console.warn(
              `Retrying component load "${componentName}" (attempt ${attemptNumber + 1}/${maxRetries}) after ${delay}ms`
            );

            setTimeout(() => {
              attemptLoad(attemptNumber + 1);
            }, delay);
          });
      };

      attemptLoad(0);
    });
  });
}

/**
 * Preload a lazy-loaded component
 * Useful for critical routes that should be loaded eagerly
 */
export function preloadComponent(
  componentImport: () => Promise<{ default: ComponentType<any> }>
): void {
  componentImport().catch((error) => {
    console.error('Failed to preload component:', error);
  });
}
